import React, { useState, useMemo } from 'react';
import {
  Table, Card, Button, Space, Typography, Form, Input, Select, Tag,
  Alert, Segmented, Drawer, message, Radio
} from 'antd';
import { SearchOutlined, ReloadOutlined, ExportOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;
const { Option } = Select;

// ────────────────────────────────────────────
//  常量
// ────────────────────────────────────────────

const STATUS_MAP = {
  processing: { text: '处理中', color: 'processing' },
  success: { text: '成功', color: 'success' },
  fail: { text: '失败', color: 'error' },
};

// 操作类型：设备置新 / 流量置空
const OPERATION_TYPE_MAP = {
  reset: { text: '设备置新', color: 'default' },
  clearTraffic: { text: '流量置空', color: 'purple' },
};
// 按天汇总同日排序：设备置新在前
const OPERATION_TYPE_ORDER = { reset: 1, clearTraffic: 2 };

const PRODUCT_OPTIONS = [
  { value: 'PROD_Q1', label: '智能摄像机Q1' },
  { value: 'PROD_P1', label: '云台机P1' },
  { value: 'PROD_F1', label: '烟雾报警器F1' },
  { value: 'PROD_D1', label: '智能门铃Pro' },
];

const ENTERPRISE_OPTIONS = [
  { value: 'ENT001', label: '深圳智创科技有限公司' },
  { value: 'ENT002', label: '杭州万物互联有限公司' },
];

// ────────────────────────────────────────────
//  Mock 数据 — TODO: 联调时替换为接口数据
//  查询契约：输入 test 即可查到结果，输入其他内容为空
//  operationType: reset=设备置新(无ICCID) / clearTraffic=流量置空(有ICCID)
// ────────────────────────────────────────────

const MOCK_RECORDS = [
  // 设备置新
  { key: '1', license: 'test-license-001', operationType: 'reset', iccid: '', productName: '智能摄像机Q1', productId: 'PROD_Q1', account: 'test-worker-zhang', status: 'success', time: '2026-07-14 15:30:00', date: '2026-07-14', enterpriseName: '深圳智创科技有限公司', enterpriseId: 'ENT001' },
  { key: '2', license: 'test-license-002', operationType: 'reset', iccid: '', productName: '云台机P1', productId: 'PROD_P1', account: 'test-worker-li', status: 'success', time: '2026-07-14 14:20:00', date: '2026-07-14', enterpriseName: '深圳智创科技有限公司', enterpriseId: 'ENT001' },
  { key: '3', license: 'test-license-003', operationType: 'reset', iccid: '', productName: '烟雾报警器F1', productId: 'PROD_F1', account: 'test-worker-wang', status: 'processing', time: '2026-07-14 16:00:00', date: '2026-07-14', enterpriseName: '杭州万物互联有限公司', enterpriseId: 'ENT002' },
  { key: '4', license: 'test-license-004', operationType: 'reset', iccid: '', productName: '智能摄像机Q1', productId: 'PROD_Q1', account: 'test-worker-zhang', status: 'fail', time: '2026-07-13 10:15:00', date: '2026-07-13', enterpriseName: '深圳智创科技有限公司', enterpriseId: 'ENT001' },
  { key: '5', license: 'test-license-005', operationType: 'reset', iccid: '', productName: '云台机P1', productId: 'PROD_P1', account: 'test-worker-li', status: 'success', time: '2026-07-13 09:30:00', date: '2026-07-13', enterpriseName: '杭州万物互联有限公司', enterpriseId: 'ENT002' },
  // 流量置空（带 ICCID）
  { key: '6', license: 'test-license-006', operationType: 'clearTraffic', iccid: '89860022110000001234', productName: '智能门铃Pro', productId: 'PROD_D1', account: 'test-worker-zhao', status: 'success', time: '2026-07-14 11:20:00', date: '2026-07-14', enterpriseName: '杭州万物互联有限公司', enterpriseId: 'ENT002' },
  { key: '7', license: 'test-license-007', operationType: 'clearTraffic', iccid: '89860022110000001235', productName: '智能摄像机Q1', productId: 'PROD_Q1', account: 'test-worker-zhang', status: 'success', time: '2026-07-13 17:45:00', date: '2026-07-13', enterpriseName: '深圳智创科技有限公司', enterpriseId: 'ENT001' },
  { key: '8', license: 'test-license-008', operationType: 'clearTraffic', iccid: '89860022110000001236', productName: '云台机P1', productId: 'PROD_P1', account: 'test-worker-li', status: 'fail', time: '2026-07-13 16:30:00', date: '2026-07-13', enterpriseName: '深圳智创科技有限公司', enterpriseId: 'ENT001' },
  { key: '9', license: 'test-license-009', operationType: 'clearTraffic', iccid: '89860022110000001237', productName: '烟雾报警器F1', productId: 'PROD_F1', account: 'test-worker-wang', status: 'processing', time: '2026-07-12 15:10:00', date: '2026-07-12', enterpriseName: '杭州万物互联有限公司', enterpriseId: 'ENT002' },
  { key: '10', license: 'test-license-010', operationType: 'clearTraffic', iccid: '89860022110000001238', productName: '智能门铃Pro', productId: 'PROD_D1', account: 'test-worker-zhao', status: 'success', time: '2026-07-11 09:20:00', date: '2026-07-11', enterpriseName: '杭州万物互联有限公司', enterpriseId: 'ENT002' },
];

// ────────────────────────────────────────────
//  列表视图表格列
// ────────────────────────────────────────────

const getListColumns = (showEnterprise) => {
  const cols = [
    {
      // License 主文本 + ICCID 副文本（仅流量置空记录展示）
      title: 'License', key: 'license', width: 240,
      render: (_, r) => (
        <div>
          <Button
            type="link"
            size="small"
            style={{ fontFamily: 'monospace', padding: 0, height: 'auto', lineHeight: '20px' }}
            onClick={() => window.open(`/device/license-query?license=${r.license}`, '_blank')}
          >
            {r.license}
          </Button>
          {r.operationType === 'clearTraffic' && r.iccid && (
            <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#999', lineHeight: '18px' }}>
              {r.iccid}
            </div>
          )}
        </div>
      ),
    },
    {
      title: '操作类型', dataIndex: 'operationType', width: 110,
      render: (v) => {
        const t = OPERATION_TYPE_MAP[v] || { text: v || '-', color: 'default' };
        return <Tag color={t.color} style={{ border: 'none' }}>{t.text}</Tag>;
      },
    },
    {
      title: '产品信息', key: 'product', width: 160,
      render: (_, r) => (
        <div>
          <div>{r.productName}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>{r.productId}</Text>
        </div>
      ),
    },
    {
      title: '产测账号', dataIndex: 'account', width: 160,
    },
  ];
  if (showEnterprise) {
    cols.push({
      title: '所属企业', key: 'enterprise', width: 180,
      render: (_, r) => (
        <div>
          <div>{r.enterpriseName}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>{r.enterpriseId}</Text>
        </div>
      ),
    });
  }
  cols.push(
    {
      title: '操作状态', dataIndex: 'status', width: 100,
      render: (v) => {
        const s = STATUS_MAP[v] || { text: v, color: 'default' };
        return <Tag color={s.color} style={{ border: 'none' }}>{s.text}</Tag>;
      },
    },
    {
      title: '操作时间', dataIndex: 'time', width: 180,
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.time.localeCompare(b.time),
      showSorterTooltip: { title: '点击排序' },
    },
  );
  return cols;
};

// ────────────────────────────────────────────
//  按天汇总视图表格列
//  聚合维度：操作日期 + 操作类型（同一天两种类型各一行）
// ────────────────────────────────────────────

const getDailyColumns = (onOpenDrawer) => [
  {
    title: '设备总数', dataIndex: 'count', width: 120,
  },
  {
    title: '操作类型', dataIndex: 'operationType', width: 120,
    render: (v) => {
      const t = OPERATION_TYPE_MAP[v] || { text: v || '-', color: 'default' };
      return <Tag color={t.color} style={{ border: 'none' }}>{t.text}</Tag>;
    },
  },
  {
    title: '操作日期', dataIndex: 'date', width: 160,
    defaultSortOrder: 'descend',
    sorter: (a, b) => a.date.localeCompare(b.date),
    showSorterTooltip: { title: '点击排序' },
  },
  {
    title: '操作', key: 'action', width: 100,
    render: (_, r) => (
      <Button type="link" size="small" onClick={() => onOpenDrawer(r.date, r.operationType)}>
        查看设备
      </Button>
    ),
  },
];

// ══════════════════════════════════════════════
//  主组件：ProdTestAfterSales
// ══════════════════════════════════════════════

const ProdTestAfterSales = () => {
  const [form] = Form.useForm();

  // Mock 角色切换
  const [mockRole, setMockRole] = useState('platform');

  // 视图模式
  const [viewMode, setViewMode] = useState('daily');

  // 筛选后的数据
  const [filteredRecords, setFilteredRecords] = useState(MOCK_RECORDS);
  const [hasQueried, setHasQueried] = useState(false);

  // 明细抽屉状态
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerDate, setDrawerDate] = useState('');
  const [drawerOperationType, setDrawerOperationType] = useState('reset');
  const [drawerRecords, setDrawerRecords] = useState([]);

  // ── 按角色过滤后的基础数据 ──
  const baseRecords = useMemo(() => {
    if (mockRole === 'enterprise') {
      return MOCK_RECORDS.filter((r) => r.enterpriseId === 'ENT001');
    }
    return MOCK_RECORDS;
  }, [mockRole]);

  // ── 按天汇总数据（操作日期 + 操作类型 聚合）──
  const dailySummary = useMemo(() => {
    const groups = {};
    filteredRecords.forEach((r) => {
      const gkey = `${r.date}_${r.operationType}`;
      if (!groups[gkey]) {
        groups[gkey] = { date: r.date, operationType: r.operationType, count: 0 };
      }
      groups[gkey].count += 1;
    });
    return Object.values(groups)
      .map((g) => ({ key: `${g.date}_${g.operationType}`, ...g }))
      .sort((a, b) => (
        b.date.localeCompare(a.date)
        || (OPERATION_TYPE_ORDER[a.operationType] ?? 9) - (OPERATION_TYPE_ORDER[b.operationType] ?? 9)
      ));
  }, [filteredRecords]);

  // ── 查询逻辑 ──
  const handleQuery = () => {
    const values = form.getFieldsValue();
    let data = [...baseRecords];

    // License 筛选
    if (values.license) {
      data = data.filter((r) => r.license.includes(values.license));
    }

    // 产品筛选
    if (values.productKeyword) {
      const productSearchType = values.productSearchType || 'name';
      if (productSearchType === 'name') {
        data = data.filter((r) => r.productId === values.productKeyword);
      } else {
        data = data.filter((r) => r.productId.includes(values.productKeyword));
      }
    }

    // 产测账号筛选
    if (values.account) {
      data = data.filter((r) => r.account.includes(values.account));
    }

    // 操作类型筛选
    if (values.operationType) {
      data = data.filter((r) => r.operationType === values.operationType);
    }

    // 所属企业筛选（仅平台角色）
    if (values.enterpriseKeyword) {
      const enterpriseSearchType = values.enterpriseSearchType || 'name';
      if (enterpriseSearchType === 'name') {
        data = data.filter((r) => r.enterpriseId === values.enterpriseKeyword);
      } else {
        data = data.filter((r) => r.enterpriseId.includes(values.enterpriseKeyword));
      }
    }

    // 操作状态筛选
    if (values.status) {
      data = data.filter((r) => r.status === values.status);
    }

    setFilteredRecords(data);
    setHasQueried(true);
  };

  // ── 重置 ──
  const handleReset = () => {
    form.resetFields();
    setFilteredRecords(baseRecords);
    setHasQueried(false);
  };

  // ── 角色切换 ──
  const handleRoleChange = (e) => {
    const role = e.target.value;
    setMockRole(role);
    form.resetFields();
    const data = role === 'enterprise'
      ? MOCK_RECORDS.filter((r) => r.enterpriseId === 'ENT001')
      : MOCK_RECORDS;
    setFilteredRecords(data);
    setHasQueried(false);
  };

  // ── 导出 ──
  const handleExport = () => {
    message.success(viewMode === 'list' ? '列表导出成功，请查看下载列表（含ICCID）' : '汇总数据导出成功，请查看下载列表');
  };

  // ── 明细抽屉导出 ──
  const handleDrawerExport = () => {
    message.success('明细数据导出成功，请查看下载列表（含ICCID）');
  };

  // ── 打开明细抽屉（日期 + 操作类型）──
  const openDrawer = (date, operationType) => {
    const records = filteredRecords.filter((r) => r.date === date && r.operationType === operationType);
    setDrawerDate(date);
    setDrawerOperationType(operationType);
    setDrawerRecords(records);
    setDrawerVisible(true);
  };

  // ── 视图切换 ──
  const handleViewChange = (val) => {
    setViewMode(val);
  };

  const isPlatform = mockRole === 'platform';

  // ── 当前表格数据 ──
  const tableData = viewMode === 'list' ? filteredRecords : dailySummary;
  const tableColumns = viewMode === 'list' ? getListColumns(isPlatform) : getDailyColumns(openDrawer);

  // ── 空状态判断 ──
  const showEmpty = hasQueried && filteredRecords.length === 0;
  const emptyText = hasQueried ? '暂无符合条件的数据' : '暂无数据';

  return (
    <div style={{ padding: 24 }}>
      {/* 标题行 + Mock角色切换 */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4} style={{ margin: 0 }}>产测售后</Title>
        <Space>
          <Text type="secondary">Mock角色：</Text>
          <Radio.Group value={mockRole} onChange={handleRoleChange} size="small">
            <Radio.Button value="platform">平台</Radio.Button>
            <Radio.Button value="enterprise">企业</Radio.Button>
          </Radio.Group>
        </Space>
      </div>

      {/* 信息提示 */}
      <Alert
        type="info"
        showIcon
        closable
        message="当前仅展示企业在平台产测工具对设备进行的置新、流量置空操作数据"
        style={{ marginBottom: 16, borderRadius: 8 }}
      />

      {/* 筛选区 */}
      <Card
        bordered={false}
        style={{ marginBottom: 8, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}
        bodyStyle={{ padding: '12px 0' }}
      >
        <Form form={form} layout="inline" style={{ flexWrap: 'wrap', gap: '8px 0' }}>
          {/* License */}
          <Form.Item name="license" style={{ marginRight: 8 }}>
            <Input placeholder="License" allowClear style={{ width: 200 }} />
          </Form.Item>

          {/* 产品筛选 */}
          <Form.Item style={{ marginRight: 8 }}>
            <Input.Group compact style={{ display: 'flex' }}>
              <Form.Item name="productSearchType" noStyle initialValue="name">
                <Select style={{ width: 100 }} onChange={() => form.setFieldsValue({ productKeyword: undefined })}>
                  <Option value="name">产品名称</Option>
                  <Option value="id">产品ID</Option>
                </Select>
              </Form.Item>
              <Form.Item shouldUpdate={(p, c) => p.productSearchType !== c.productSearchType} noStyle>
                {({ getFieldValue }) => {
                  const type = getFieldValue('productSearchType') || 'name';
                  return type === 'name' ? (
                    <Form.Item name="productKeyword" noStyle>
                      <Select placeholder="请选择产品" allowClear showSearch optionFilterProp="label" style={{ width: 180 }} options={PRODUCT_OPTIONS} />
                    </Form.Item>
                  ) : (
                    <Form.Item name="productKeyword" noStyle>
                      <Input placeholder="产品ID" allowClear style={{ width: 180 }} />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Input.Group>
          </Form.Item>

          {/* 产测账号 */}
          <Form.Item name="account" style={{ marginRight: 8 }}>
            <Input placeholder="产测账号" allowClear style={{ width: 160 }} />
          </Form.Item>

          {/* 操作类型筛选 */}
          <Form.Item name="operationType" style={{ marginRight: 8 }}>
            <Select placeholder="操作类型" allowClear style={{ width: 130 }}>
              <Option value="">全部</Option>
              <Option value="reset">设备置新</Option>
              <Option value="clearTraffic">流量置空</Option>
            </Select>
          </Form.Item>

          {/* 所属企业筛选 — 仅平台角色可见 */}
          {isPlatform && (
            <Form.Item style={{ marginRight: 8 }}>
              <Input.Group compact style={{ display: 'flex' }}>
                <Form.Item name="enterpriseSearchType" noStyle initialValue="name">
                  <Select style={{ width: 100 }} onChange={() => form.setFieldsValue({ enterpriseKeyword: undefined })}>
                    <Option value="name">企业名称</Option>
                    <Option value="id">企业ID</Option>
                  </Select>
                </Form.Item>
                <Form.Item shouldUpdate={(p, c) => p.enterpriseSearchType !== c.enterpriseSearchType} noStyle>
                  {({ getFieldValue }) => {
                    const type = getFieldValue('enterpriseSearchType') || 'name';
                    return type === 'name' ? (
                      <Form.Item name="enterpriseKeyword" noStyle>
                        <Select placeholder="请选择企业" allowClear showSearch optionFilterProp="label" style={{ width: 180 }} options={ENTERPRISE_OPTIONS} />
                      </Form.Item>
                    ) : (
                      <Form.Item name="enterpriseKeyword" noStyle>
                        <Input placeholder="企业ID" allowClear style={{ width: 180 }} />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </Input.Group>
            </Form.Item>
          )}

          {/* 操作状态 */}
          <Form.Item name="status" style={{ marginRight: 8 }}>
            <Select placeholder="操作状态" allowClear style={{ width: 130 }}>
              <Option value="">全部</Option>
              <Option value="processing">处理中</Option>
              <Option value="success">成功</Option>
              <Option value="fail">失败</Option>
            </Select>
          </Form.Item>

          {/* 操作按钮 */}
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery}>查询</Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* 表格区 */}
      <Card bordered={false}>
        <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Segmented
            value={viewMode}
            onChange={handleViewChange}
            options={[
              { label: '列表视图', value: 'list' },
              { label: '按天汇总', value: 'daily' },
            ]}
          />
          <Button icon={<ExportOutlined />} onClick={handleExport}>导出</Button>
        </div>
        <Table
          columns={tableColumns}
          dataSource={tableData}
          rowKey="key"
          scroll={{ x: viewMode === 'list' ? (isPlatform ? 1180 : 1000) : 520 }}
          pagination={{
            showSizeChanger: true,
            showTotal: (t) => `共 ${t} 条记录`,
          }}
          locale={{ emptyText: showEmpty ? emptyText : (hasQueried ? emptyText : '暂无数据') }}
        />
      </Card>

      {/* 明细抽屉（按 日期 + 操作类型 追溯） */}
      <Drawer
        title={`操作明细 — ${drawerDate} ${(OPERATION_TYPE_MAP[drawerOperationType] || {}).text || ''}`}
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        width={1000}
        destroyOnClose
      >
        <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end' }}>
          <Button icon={<ExportOutlined />} onClick={handleDrawerExport}>导出</Button>
        </div>
        <Table
          columns={getListColumns(isPlatform)}
          dataSource={drawerRecords}
          rowKey="key"
          scroll={{ x: isPlatform ? 1180 : 1000 }}
          pagination={{
            showSizeChanger: true,
            showTotal: (t) => `共 ${t} 条记录`,
          }}
          locale={{ emptyText: '暂无明细数据' }}
        />
      </Drawer>
    </div>
  );
};

export default ProdTestAfterSales;
